import omit from 'lodash/omit';
import RedirectActions from 'app/actions/RedirectActions';
import { getAppConfig } from 'app/selectors/AppSelector';
import composeFailureMessaging from '../helpers/messaging/composeFailureMessaging';
import composeGlobalFailureMessaging from '../helpers/messaging/composeGlobalFailureMessaging';

const AUTH_ERROR_CLASSIFICATIONS = ['invalid_token', 'RailsSessionInvalidationException'];

/**
 * Middleware for making and handling asynchronous requests to the API Gateway.
 *
 * Handles actions with the following properties:
 *  apiRequest:         A function to make the API request, with the signature `(state, apiClient)`.
 *                        Should return an `Observable`.
 *  types:              [BEFORE_REQUEST, SUCCESS, FAILURE], to be used as the action's `type`
 *                        during the request and when it completes.
 *  apiSuccess:         A function which can be used modify the `SUCCESS` action before being passed along,
 *                        with the signature `(responseBody, store)`.
 *  apiFailure:         A function which can be used modify the `FAILURE` action before being passed along,
 *                        with the signature `(responseBody, store)`.
 *  postSuccess:        A function which takes the `state`, and returns an `action` object,
 *                        to be dispatched separately when the request succeeds.
 *
 * NB: BEFORE_REQUEST, as implied, is called before `apiRequest()` is called.
 * This fact is very important if you have reducers that modify the state in
 * response to BEFORE_REQUEST, as `apiRequest()` will be passed the modified
 * state.
 *
 * @param {object} apiClient
 */
export default function apiRequestMiddleware(apiClient) {
  return store => next => action => {
    if (!action.apiRequest) {
      return next(action);
    }

    const { apiRequest, apiSuccess, apiFailure, types, middlewareData, postSuccess, postFailure, ...rest } = action;

    const originalPayload = rest.payload || {};
    const [BEFORE_REQUEST, SUCCESS, FAILURE] = types;
    const basicMiddleware = omit(middlewareData, ['gaEvent', 'appEvent', 'gtmEvent']);

    if (!BEFORE_REQUEST) {
      throw new Error(`Expected "action.types[0]" to be defined. Found [${types.join(', ')}]`);
    }

    if (!SUCCESS) {
      throw new Error(`Expected "action.types[1]" to be defined. Found [${types.join(', ')}]`);
    }

    if (!FAILURE) {
      throw new Error(`Expected "action.types[2]" to be defined. Found [${types.join(', ')}]`);
    }

    // Dispatch a BEFORE_REQUEST action and kick off the request
    store.dispatch({
      ...rest,
      originalPayload,
      middlewareData: basicMiddleware,
      type: BEFORE_REQUEST
    });

    const promise = apiRequest(store.getState(), apiClient).toPromise();
    return promise
      .catch(({ response, status }) => {
        const res = response || {};
        const body = res.body || {};
        const { error } = body;

        const appConfig = getAppConfig(store.getState());
        const appRequiresAuth = appConfig.get('authRequired');
        const appRequiresAccount = appConfig.get('accountRequired');
        const sessionExpired = AUTH_ERROR_CLASSIFICATIONS.includes(error?.classification);

        // Redirect to the login page when the session expires and if the app
        // requires authentication or a library account. (eg. borrowing pages)
        if (__CLIENT__ && sessionExpired && (appRequiresAuth || appRequiresAccount)) {
          const destination = `${window.location.pathname}${window.location.search}`;
          store.dispatch(RedirectActions.redirect(`/user/login?destination=${encodeURIComponent(destination)}`));
          return;
        }

        // Default messaging for non-GET requests
        const method = res.error?.method || res.request?.method;
        const defaultMessaging =
          method === 'GET' || error?.fieldErrors
            ? {}
            : {
                messaging: action.messagingId
                  ? composeFailureMessaging(action.messagingId, error)
                  : composeGlobalFailureMessaging(error)
              };

        const failureAction = {
          ...defaultMessaging,
          ...rest,
          middlewareData,
          sessionExpired,
          type: FAILURE,
          payload: {
            ...originalPayload,
            status,
            ...body,
            postLoginAction: action // In case the action failed because the session expired
          }
        };

        // Allow the failure action to be modified before passing along.
        if (apiFailure) {
          store.dispatch({ ...failureAction, ...apiFailure(body, store) });
        } else {
          store.dispatch(failureAction);
        }

        if (postFailure) {
          const postFailureAction = postFailure(error, store.getState());
          if (postFailureAction) {
            store.dispatch(postFailureAction);
          }
        }

        // IMPORTANT: This will cause 500 errors from the API to be reported appropriately during server-side rendering.
        const { ignoreServerErrors } = middlewareData || {};
        if (__SERVER__ && res.serverError && !ignoreServerErrors) {
          throw failureAction;
        }
      })
      .then(response => {
        if (!response) return;

        const successAction = {
          ...rest,
          middlewareData,
          type: SUCCESS,
          payload: {
            ...originalPayload,
            ...(response.body || {})
          }
        };

        // Allow the success action to be modified before passing along.
        if (apiSuccess) {
          store.dispatch({ ...successAction, ...apiSuccess(response.body, store) });
        } else {
          store.dispatch(successAction);
        }

        if (postSuccess) {
          const postSuccessAction = postSuccess(store.getState());
          if (postSuccessAction) {
            store.dispatch(postSuccessAction);
          }
        }
      });
  };
}
